/**
 * Data layer for `multiple-landing-pages-per-offer`. Thin typed fetch wrappers over /api/v1/multiple-landing-pages-per-offer.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export interface MultipleLandingPagesPerOfferRow {
	id: string;
	tenant_id: string;
	created_at: number;
	updated_at: number;
	[key: string]: unknown;
}

export interface ListMultipleLandingPagesPerOfferResponse {
	count: number;
	items: MultipleLandingPagesPerOfferRow[];
}

export interface CreateMultipleLandingPagesPerOfferBody {
	[key: string]: unknown;
}

const BASE = "/api/v1/multiple-landing-pages-per-offer";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListMultipleLandingPagesPerOfferResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListMultipleLandingPagesPerOfferResponse>(res);
}

export async function create(body: CreateMultipleLandingPagesPerOfferBody): Promise<MultipleLandingPagesPerOfferRow> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body ?? {}),
	});
	return parseOrThrow<MultipleLandingPagesPerOfferRow>(res);
}

export async function remove(id: string): Promise<{ removed: boolean }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ removed: boolean }>(res);
}

export const multipleLandingPagesPerOfferQueryKeys = {
	all: ["multiple-landing-pages-per-offer"] as const,
	list: () => [...multipleLandingPagesPerOfferQueryKeys.all] as const,
	detail: (id: string) => [...multipleLandingPagesPerOfferQueryKeys.all, id] as const,
};
